import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Bell, 
  MessageSquare, 
  Mail, 
  Smartphone,
  Clock,
  FileText,
  Plus,
  Edit2,
  Trash2,
  Play,
  Pause,
  ToggleLeft,
  Settings2,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';

const reminderRules = [
  {
    id: 1,
    name: 'Missed Session Alert',
    trigger: 'Patient misses scheduled session',
    channels: ['push', 'whatsapp'],
    frequency: 'Immediate + 24h follow-up',
    status: 'active',
    performance: { sent: 342, opened: 298, actioned: 187 }
  },
  {
    id: 2,
    name: 'Low Engagement Warning',
    trigger: 'Engagement score drops below 60%',
    channels: ['email', 'push'],
    frequency: 'Daily digest',
    status: 'active',
    performance: { sent: 156, opened: 124, actioned: 89 }
  },
  {
    id: 3,
    name: 'High Pain Score Follow-up',
    trigger: 'Pain score > 7 reported',
    channels: ['push', 'whatsapp'],
    frequency: 'Immediate',
    status: 'active',
    performance: { sent: 78, opened: 72, actioned: 65 }
  },
  {
    id: 4,
    name: 'Exercise Reminder',
    trigger: 'Daily exercise not completed',
    channels: ['push'],
    frequency: 'Evening (6 PM)',
    status: 'paused',
    performance: { sent: 1240, opened: 890, actioned: 567 }
  },
];

const templates = [
  {
    id: 1,
    name: 'Supportive Reminder',
    tone: 'supportive',
    preview: "Hi {name}, we noticed you missed your session yesterday. We understand life gets busy! Ready to reschedule? Your recovery journey is important to us. 💪",
    useCount: 1250
  },
  {
    id: 2,
    name: 'Professional Follow-up',
    tone: 'professional',
    preview: "Dear {name}, this is a reminder about your upcoming physiotherapy session scheduled for {date} at {time}. Please confirm your attendance.",
    useCount: 890
  },
  {
    id: 3,
    name: 'Motivational Push',
    tone: 'motivational',
    preview: "You're doing amazing, {name}! Just {days} more days of consistent exercises to reach your weekly goal. Keep going! 🎯",
    useCount: 2100
  },
];

export default function ComplianceConsole() {
  const [selectedTab, setSelectedTab] = useState('rules');
  const [showNewRule, setShowNewRule] = useState(false);

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'push': return Smartphone;
      case 'whatsapp': return MessageSquare;
      case 'email': return Mail;
      default: return Bell;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Compliance Engine</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure automated reminders and engagement rules</p>
        </div>
        <Button onClick={() => setShowNewRule(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Rule
        </Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Reminders Sent', value: '12,847', change: '+15% this week' },
          { label: 'Open Rate', value: '82.4%', change: '+3.2% vs avg' },
          { label: 'Action Rate', value: '58.7%', change: '+5.1% vs avg' },
          { label: 'Compliance Lift', value: '+12.3%', change: 'From baseline' },
        ].map((stat, idx) => (
          <Card key={idx}>
            <CardContent className="pt-5">
              <p className="text-sm text-muted-foreground">{stat.label}</p>
              <p className="text-2xl font-display font-bold text-foreground mt-1">{stat.value}</p>
              <p className="text-xs text-success mt-1">{stat.change}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Main Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList>
          <TabsTrigger value="rules">Automation Rules</TabsTrigger>
          <TabsTrigger value="templates">Message Templates</TabsTrigger>
          <TabsTrigger value="channels">Channel Settings</TabsTrigger>
          <TabsTrigger value="abtests">A/B Tests</TabsTrigger>
        </TabsList>
        
        <TabsContent value="rules" className="mt-6 space-y-4">
          {reminderRules.map((rule, idx) => (
            <motion.div
              key={rule.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card>
                <CardContent className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-foreground">{rule.name}</h3>
                        <Badge variant={rule.status === 'active' ? 'default' : 'secondary'}>
                          {rule.status === 'active' ? 'Active' : 'Paused'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        <span className="font-medium">Trigger:</span> {rule.trigger}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Channels:</span>
                          <div className="flex gap-1">
                            {rule.channels.map((channel) => {
                              const Icon = getChannelIcon(channel);
                              return (
                                <div key={channel} className="w-6 h-6 rounded bg-muted flex items-center justify-center">
                                  <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">{rule.frequency}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 lg:gap-6">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-lg font-semibold text-foreground">{rule.performance.sent}</p>
                          <p className="text-xs text-muted-foreground">Sent</p>
                        </div>
                        <div>
                          <p className="text-lg font-semibold text-foreground">{rule.performance.opened}</p>
                          <p className="text-xs text-muted-foreground">Opened</p>
                        </div>
                        <div>
                          <p className="text-lg font-semibold text-success">{rule.performance.actioned}</p>
                          <p className="text-xs text-muted-foreground">Actioned</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon">
                          {rule.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="icon">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </TabsContent>
        
        <TabsContent value="templates" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Button variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {templates.map((template, idx) => (
              <Card key={template.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    <Badge variant="outline" className="capitalize">{template.tone}</Badge>
                  </div>
                  <CardDescription>Used {template.useCount.toLocaleString()} times</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                    {template.preview}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button variant="outline" size="sm" className="flex-1">
                      <Edit2 className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1">
                      <FileText className="w-3 h-3 mr-1" /> Preview
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
        
        <TabsContent value="channels" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              { name: 'Push Notifications', icon: Smartphone, enabled: true, dailyLimit: 5 },
              { name: 'WhatsApp', icon: MessageSquare, enabled: true, dailyLimit: 3 },
              { name: 'Email', icon: Mail, enabled: true, dailyLimit: 2 },
            ].map((channel, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-light flex items-center justify-center">
                        <channel.icon className="w-5 h-5 text-primary" />
                      </div>
                      <CardTitle className="text-base">{channel.name}</CardTitle>
                    </div>
                    <Switch checked={channel.enabled} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm">Daily Limit per Patient</Label>
                    <Input type="number" defaultValue={channel.dailyLimit} className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-sm">Quiet Hours</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input type="time" defaultValue="22:00" className="flex-1" />
                      <span className="self-center text-muted-foreground">to</span>
                      <Input type="time" defaultValue="08:00" className="flex-1" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
        
        <TabsContent value="abtests" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>A/B Testing</CardTitle>
              <CardDescription>Compare different reminder strategies</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-success-light flex items-center justify-center">
                        <Zap className="w-4 h-4 text-success" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">Supportive vs Professional Tone</h4>
                        <p className="text-xs text-muted-foreground">Running for 14 days • 2,400 patients</p>
                      </div>
                    </div>
                    <Badge className="bg-success text-success-foreground">Winner: Supportive (+18%)</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm font-medium text-foreground mb-1">Variant A: Supportive</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-muted-foreground">Open: <span className="text-foreground">87%</span></span>
                        <span className="text-muted-foreground">Action: <span className="text-success">64%</span></span>
                      </div>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm font-medium text-foreground mb-1">Variant B: Professional</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-muted-foreground">Open: <span className="text-foreground">79%</span></span>
                        <span className="text-muted-foreground">Action: <span className="text-foreground">46%</span></span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <Button variant="outline" className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Create New A/B Test
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* New Rule Dialog */}
      <Dialog open={showNewRule} onOpenChange={setShowNewRule}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Automation Rule</DialogTitle>
            <DialogDescription>Set up a new compliance reminder rule</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Rule Name</Label>
              <Input placeholder="e.g., Missed Session Alert" className="mt-1.5" />
            </div>
            
            <div>
              <Label>Trigger Condition</Label>
              <Select>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select trigger" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="missed">Missed session</SelectItem>
                  <SelectItem value="low-engagement">Low engagement score</SelectItem>
                  <SelectItem value="high-pain">High pain score</SelectItem>
                  <SelectItem value="exercise">Exercise not completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Channels</Label>
              <div className="flex gap-2 mt-1.5">
                <Button variant="outline" className="flex-1"><Smartphone className="w-4 h-4 mr-2" /> Push</Button>
                <Button variant="outline" className="flex-1"><MessageSquare className="w-4 h-4 mr-2" /> WhatsApp</Button>
                <Button variant="outline" className="flex-1"><Mail className="w-4 h-4 mr-2" /> Email</Button>
              </div>
            </div>
            
            <div>
              <Label>Message Template</Label>
              <Textarea placeholder="Enter your message..." className="mt-1.5" rows={3} />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRule(false)}>Cancel</Button>
            <Button>Create Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
